/**
 * File: ChatThread.tsx
 * Description: Optimized thread component for assistant-ui with improved streaming and scrolling
 * Author: System
 * Created: 2025-09-03
 * Last Modified: 2025-10-02
 */

import {
  ActionBarPrimitive,
  ComposerPrimitive,
  MessagePrimitive,
  MessagePartPrimitive,
  ThreadPrimitive,
  useThread,
  useAssistantRuntime,
} from '@assistant-ui/react';
import {
  ArrowDownIcon,
  ArrowPathIcon,
  CheckIcon,
  DocumentDuplicateIcon,
  PaperAirplaneIcon,
  SparklesIcon,
  XMarkIcon,
  UserIcon,
} from '@heroicons/react/24/outline';
import React, { createContext, useContext, useState } from 'react';
import type { FC } from 'react';

import { useChatActions } from '../../hooks/useChatActions';

import { FeedbackButtons } from './feedback';
import { MarkdownText } from './MarkdownText';
import { TooltipIconButton } from './TooltipIconButton';

// Context for project information
interface ProjectContextType {
  projectId?: number | null;
}

const ProjectContext = createContext<ProjectContextType>({});

const useProjectContext = () => useContext(ProjectContext);

// Wrapper component that adapts MessagePartPrimitive.Text children to MarkdownText content prop
const MarkdownWrapper: FC<{ children: string }> = ({ children }) => (
  <MarkdownText content={children} />
);

// Text component for rendering markdown content with smooth streaming
const MessageText: FC = () => (
  <MessagePartPrimitive.Text
    smooth={true}
    component={MarkdownWrapper}
  />
);

interface ChatThreadProps {
  projectId?: number | null;
}

const CircleStopIcon: FC = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 16 16"
    fill="currentColor"
    width="16"
    height="16"
  >
    <rect width="10" height="10" x="3" y="3" rx="2" />
  </svg>
);

export const ChatThread: FC<ChatThreadProps> = ({ projectId }) => {
  return (
    <ProjectContext.Provider value={{ projectId }}>
      <ThreadPrimitive.Root className="h-full flex flex-col">
        <ThreadPrimitive.Viewport
          className="flex-1 overflow-y-auto min-h-0 relative"
          autoScroll={true}
        >
          <MessagesContainer />
          <ThreadWelcome />
          <ThreadScrollToBottom />
        </ThreadPrimitive.Viewport>
        <div className="p-4 border-t border-base-300 bg-base-100 flex-shrink-0">
          <ConfirmationBanner />
          <Composer />
          <div className="text-xs text-base-content/60 mt-2 text-center">
            AI-generated content may be incorrect. Check important info.
          </div>
        </div>
      </ThreadPrimitive.Root>
    </ProjectContext.Provider>
  );
};

const MessagesContainer: FC = () => {
  return (
    <div className="flex-1 p-4 relative min-h-0">
      <ThreadPrimitive.Messages
        components={{
          UserMessage,
          AssistantMessage,
        }}
      />
    </div>
  );
};

const ThreadScrollToBottom: FC = () => {
  return (
    <ThreadPrimitive.ScrollToBottom asChild>
      <TooltipIconButton
        tooltip="Scroll to bottom"
        variant="outline"
        className="absolute bottom-4 right-4 z-10 rounded-full shadow-lg bg-base-100 border-base-300 hover:bg-base-200 disabled:invisible"
      >
        <ArrowDownIcon className="h-4 w-4" />
      </TooltipIconButton>
    </ThreadPrimitive.ScrollToBottom>
  );
};

const ThreadWelcome: FC = () => {
  return (
    <ThreadPrimitive.Empty>
      <div className="flex flex-1 flex-col items-center justify-center p-4 text-center">
        <div className="w-12 h-12 rounded-full bg-primary text-primary-content flex items-center justify-center mb-4">
          <SparklesIcon className="w-6 h-6" />
        </div>
        <h2 className="text-lg font-semibold mb-2 text-base-content">Welcome to Archie AI</h2>
        <p className="text-base-content/70 text-sm max-w-xs mb-4">
          I&apos;m here to help you with architecture decisions, ADRs, and project context.
        </p>
        <ThreadWelcomeSuggestions />
      </div>
    </ThreadPrimitive.Empty>
  );
};

const ThreadWelcomeSuggestions: FC = () => {
  return (
    <div className="flex flex-col gap-2 w-full max-w-xs">
      <ThreadPrimitive.Suggestion
        className="rounded-lg border border-base-300 bg-base-200 p-2 text-center transition-colors hover:bg-base-300 cursor-pointer"
        prompt="Help me understand the current architecture decisions"
        method="replace"
        autoSend
      >
        <span className="text-xs text-base-content">Architecture decisions</span>
      </ThreadPrimitive.Suggestion>
      <ThreadPrimitive.Suggestion
        className="rounded-lg border border-base-300 bg-base-200 p-2 text-center transition-colors hover:bg-base-300 cursor-pointer"
        prompt="What context information is available?"
        method="replace"
        autoSend
      >
        <span className="text-xs text-base-content">Project context</span>
      </ThreadPrimitive.Suggestion>
      <ThreadPrimitive.Suggestion
        className="rounded-lg border border-base-300 bg-base-200 p-2 text-center transition-colors hover:bg-base-300 cursor-pointer"
        prompt="Generate a new ADR"
        method="replace"
        autoSend
      >
        <span className="text-xs text-base-content">New ADR</span>
      </ThreadPrimitive.Suggestion>
    </div>
  );
};

const Composer: FC = () => {
  return (
    <ComposerPrimitive.Root className="flex w-full items-end rounded-lg border border-base-300 bg-base-100 shadow-sm focus-within:border-primary/50">
      <ComposerPrimitive.Input
        rows={1}
        placeholder="Ask Archie..."
        className="flex-grow resize-none border-none bg-transparent px-3 py-2 text-sm text-base-content placeholder:text-base-content/50 outline-none focus:ring-0"
      />
      <ComposerAction />
    </ComposerPrimitive.Root>
  );
};

const ComposerAction: FC = () => {
  return (
    <div className="p-2">
      <ThreadPrimitive.If running={false}>
        <ComposerPrimitive.Send asChild>
          <TooltipIconButton
            tooltip="Send"
            variant="default"
            className="size-8 p-2"
          >
            <PaperAirplaneIcon className="h-4 w-4" />
          </TooltipIconButton>
        </ComposerPrimitive.Send>
      </ThreadPrimitive.If>
      <ThreadPrimitive.If running>
        <ComposerPrimitive.Cancel asChild>
          <TooltipIconButton
            tooltip="Cancel"
            variant="default"
            className="size-8 p-2"
          >
            <CircleStopIcon />
          </TooltipIconButton>
        </ComposerPrimitive.Cancel>
      </ThreadPrimitive.If>
    </div>
  );
};

const UserMessage: FC = () => {
  return (
    <MessagePrimitive.Root className="mb-4">
      <div className="flex items-start gap-3 justify-end">
        <div className="bg-gradient-to-br from-primary to-primary/90 text-primary-content rounded-xl px-4 py-3 max-w-[75%] min-w-0 overflow-hidden shadow-sm border border-primary/20">
          <div className="font-medium text-sm break-words [overflow-wrap:anywhere]">
            <MessagePrimitive.Parts />
          </div>
        </div>
        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-neutral to-neutral/90 text-neutral-content flex items-center justify-center flex-shrink-0 shadow-sm">
          <UserIcon className="w-4 h-4" />
        </div>
      </div>
      <UserActionBar />
    </MessagePrimitive.Root>
  );
};

const UserActionBar: FC = () => {
  return (
    <ActionBarPrimitive.Root
      hideWhenRunning
      autohide="not-last"
      className="flex justify-end mt-1 mr-12 opacity-0 hover:opacity-100 transition-opacity"
    >
      <ActionBarPrimitive.Edit asChild>
        <button className="text-xs text-base-content/50 hover:text-base-content">
          Edit
        </button>
      </ActionBarPrimitive.Edit>
    </ActionBarPrimitive.Root>
  );
};

const AssistantMessage: FC = () => {
  return (
    <MessagePrimitive.Root className="mb-4">
      <div className="flex items-start gap-2">
        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-primary/90 text-primary-content flex items-center justify-center flex-shrink-0 shadow-sm">
          <SparklesIcon className="w-4 h-4" />
        </div>
        <div className="bg-base-200/80 text-base-content rounded-lg px-2 py-1.5 max-w-[85%] min-w-0 overflow-hidden border border-base-300/50">
          <MessagePrimitive.Parts
            components={{
              Text: MessageText,
            }}
          />
        </div>
      </div>
      <AssistantActionBar />
    </MessagePrimitive.Root>
  );
};

const AssistantActionBar: FC = () => {
  const { projectId } = useProjectContext();

  // Capture assistant runtime context for rich feedback
  const thread = useThread();
  const runtime = useAssistantRuntime();

  // Extract tool calls from chat history
  const extractToolCalls = (messages: readonly any[]) => {
    const toolCalls: any[] = [];
    messages.forEach((message: any) => {
      if (message.content) {
        message.content.forEach((part: any) => {
          if (part.type === 'tool-call') {
            toolCalls.push({
              toolCallId: part.toolCallId,
              toolName: part.toolName,
              args: part.args,
              result: part.result,
              status: part.status || 'completed',
            });
          }
        });
      }
    });
    return toolCalls;
  };

  // Prepare runtime context for FeedbackButtons
  const assistantRuntimeContext = {
    chatHistory: thread?.messages ? [...thread.messages] : [],
    toolCalls: extractToolCalls(thread?.messages || []),
    threadId: undefined, // Thread ID not directly available
    messagesCount: thread?.messages?.length || 0,
    toolCallsCount: extractToolCalls(thread?.messages || []).length,
  };

  const handleFeedback = (messageId: string | undefined, type: 'up' | 'down', comment: string, includeContext: boolean, includeUserName: boolean) => {

  };

  return (
    <ActionBarPrimitive.Root
      hideWhenRunning
      autohide="not-last"
      className="col-start-2 col-end-3 mr-auto mt-1 flex gap-1"
    >
      <ActionBarPrimitive.Copy asChild>
        <TooltipIconButton tooltip="Copy" variant="ghost" className="h-6 w-6">
          <MessagePrimitive.If copied>
            <CheckIcon className="h-3 w-3" />
          </MessagePrimitive.If>
          <MessagePrimitive.If copied={false}>
            <DocumentDuplicateIcon className="h-3 w-3" />
          </MessagePrimitive.If>
        </TooltipIconButton>
      </ActionBarPrimitive.Copy>
      <ActionBarPrimitive.Reload asChild>
        <TooltipIconButton tooltip="Refresh" variant="ghost" className="h-6 w-6">
          <ArrowPathIcon className="h-3 w-3" />
        </TooltipIconButton>
      </ActionBarPrimitive.Reload>
      <FeedbackButtons
        projectId={projectId || -1}
        feedbackContext={{
          type: 'chat-message',
          data: {
            messageId: `chat-${Date.now()}`,
            messageContent: 'Chat conversation feedback',
          },
        }}
        assistantRuntimeContext={assistantRuntimeContext}
        onFeedback={handleFeedback}
      />
    </ActionBarPrimitive.Root>
  );
};

const ConfirmationBanner: FC = () => {
  const { pendingConfirmations, confirmPendingAction, dismissPendingAction, processingActionId } = useChatActions();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const currentAction = pendingConfirmations.length > 0 ? pendingConfirmations[0] : null;

  if (!currentAction) {
    return null;
  }

  const isProcessing = processingActionId === currentAction.internalId;

  const handleConfirm = async () => {
    setErrorMessage(null);
    try {
      await confirmPendingAction(currentAction);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to complete the action.';
      setErrorMessage(message);
    }
  };

  const handleDismiss = () => {
    setErrorMessage(null);
    dismissPendingAction(currentAction);
  };

  return (
    <div className="mb-3 rounded-lg border border-primary/30 bg-primary/10 p-3 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2 text-primary">
          {isProcessing ? (
            <ArrowPathIcon className="h-4 w-4 animate-spin" />
          ) : (
            <SparklesIcon className="h-4 w-4" />
          )}
          <div>
            <p className="text-[10px] uppercase font-semibold tracking-wide text-primary/70">
              {isProcessing ? 'Processing...' : 'Confirm action'}
            </p>
            <h3 className="text-sm font-semibold leading-tight text-primary">{currentAction.title}</h3>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {!isProcessing && (
            <>
              <button
                type="button"
                className="btn btn-primary btn-xs"
                onClick={handleConfirm}
                aria-label="Confirm"
                title="Confirm"
              >
                <CheckIcon className="h-4 w-4" />
              </button>
              <button
                type="button"
                className="btn btn-ghost btn-xs"
                onClick={handleDismiss}
                aria-label="Skip"
                title="Skip"
              >
                <XMarkIcon className="h-4 w-4" />
                <span className="sr-only">Skip</span>
              </button>
            </>
          )}
        </div>
      </div>
      {errorMessage ? (
        <p className="mt-2 text-[11px] text-error">{errorMessage}</p>
      ) : null}
    </div>
  );
};
